import { Badge } from 'src/design/atoms/Badge';
import { Group } from 'src/design/atoms/Group';
import { Text } from 'src/design/atoms/Text';
import { Tooltip } from 'src/design/atoms/Tooltip';
import { IconCircleCheckFilled, IconCircleX } from '@tabler/icons-react';
import { MRT_ColumnDef } from 'mantine-react-table';
import React, { useMemo } from 'react';
import {
  MilestonePhaseItemDto,
  OrganizationRoleDto,
} from 'src/api/FiretrolApiClient';
import { useToggleMilestoneItemComplete } from 'src/api/queries/milestones/useMilestoneItemMutations';
import { MilestoneMoveActionIcons } from 'src/app/milestones/MilestoneMoveActionIcons';
import {
  MILESTONE_ITEM_DEFAULT_SORTING,
  milestoneItemOrderColumn,
  milestonePhaseOrderSortingFn,
} from 'src/app/milestones/milestoneItemOrder';
import { useMilestoneItemMove } from 'src/app/milestones/useMilestoneItemMove';
import { useJobs } from 'src/hooks/useJobs';
import { BasicActionIcon } from 'src/design/atoms/BasicActionIcon';
import { CopyActionIcon } from 'src/design/atoms/CopyActionIcon';
import { EditActionIcon } from 'src/design/atoms/EditActionIcon';
import { CreateButton } from 'src/design/atoms/CreateButton';
import { BadgeShortList } from 'src/design/molecules/BadgeShortList';
import {
  useTable,
  useTableState,
} from 'src/design/organisms/DataTable/useTable';
import { useTableExports } from 'src/design/organisms/useTableExports';

interface IMilestonePhaseItemTable {
  jobId: number | string;
  data: MilestonePhaseItemDto[];
  onRowClick: (item: MilestonePhaseItemDto) => void;
  onCopyClick: (item: MilestonePhaseItemDto) => void;
  onCreateClick: () => void;
  loading?: boolean;
}

export const MilestonePhaseItemTable: React.FC<IMilestonePhaseItemTable> = ({
  jobId,
  data,
  onRowClick,
  onCopyClick,
  onCreateClick,
  loading = false,
}) => {
  const { selectedJob } = useJobs();
  const toggleComplete = useToggleMilestoneItemComplete();
  const columns = useMemo<MRT_ColumnDef<MilestonePhaseItemDto>[]>(
    () => [
      {
        accessorKey: 'title',
        header: 'Title',
        size: 250,
        enableEditing: true,
      },
      {
        id: 'critical',
        header: 'Critical',
        size: 125,
        accessorFn: (row) => (row.critical ? 'Yes' : 'No'),
        enableEditing: false,
      },
      {
        id: 'complete',
        header: 'Complete',
        size: 125,
        accessorFn: (row) => (row.complete ? 'Yes' : 'No'),
        enableEditing: true,
      },
      {
        id: 'require_association',
        header: 'Driver',
        size: 125,
        accessorFn: (row) => (row.requireAssociation ? 'Yes' : 'No'),
        enableEditing: false,
      },
      {
        accessorKey: 'phase.title',
        header: 'Phase',
        enableEditing: false,
      },
      {
        accessorKey: 'assignedTo',
        header: 'Assigned To',
        size: 150,
        Cell: ({ cell }) => {
          const roles: OrganizationRoleDto[] =
            cell.getValue<OrganizationRoleDto[]>();
          const badges = roles?.map((role) => (
            <Badge key={role.id}>{role.name}</Badge>
          ));
          return <BadgeShortList items={badges} maxItems={1} />;
        },
        enableEditing: false,
      },
      {
        accessorKey: 'description',
        header: 'Description',
        size: 300,
        enableEditing: true,
        Cell: ({ cell, column }) => {
          const description = cell.getValue<string | undefined>();
          if (!description?.trim()) return null;
          return (
            <Tooltip
              label={description}
              multiline
              w={420}
              openDelay={300}
              style={{ whiteSpace: 'pre-wrap' }}
            >
              <Text size="sm" c="bright" truncate="end" maw={column.getSize()}>
                {description}
              </Text>
            </Tooltip>
          );
        },
      },
      {
        accessorKey: 'phase.order',
        header: 'Phase Order',
        enableEditing: false,
        sortingFn: milestonePhaseOrderSortingFn,
      },
      milestoneItemOrderColumn,
    ],
    [],
  );

  const tableState = useTableState({
    identifier: 'milestone-items-ordered',
    columns: columns,
    defaultSorting: MILESTONE_ITEM_DEFAULT_SORTING,
  });

  const getMoveProps = useMilestoneItemMove({
    items: data,
    scope: { jobId: Number(jobId) },
    sorting: tableState.sorting,
    columnFilters: tableState.columnFilters,
    globalFilter: tableState.globalFilter,
  });

  const tableExports = useTableExports<MilestonePhaseItemDto>(
    'milestone-items',
    (data) => {
      return data.map((item) => ({
        ['Job Name']: selectedJob?.name,
        ['Job Number']: selectedJob?.jobNumber,
        ['Title']: item.title,
        ['Critical']: item.critical ? 'Yes' : 'No',
        ['Complete']: item.complete ? 'Yes' : 'No',
        ['Driver']: item.requireAssociation ? 'Yes' : 'No',
        ['Roles']:
          item.assignedTo
            ?.map((r: OrganizationRoleDto) => r.name)
            .join(' | ') || '',
        ['Phase']: item.phase?.title || '',
      }));
    },
  );

  const { table } = useTable({
    ...tableState,
    columnVisibility: { ...tableState.columnVisibility, order: false },
    tableOptions: {
      enableRowSelection: false,
      enableRowActions: true,
      displayColumnDefOptions: { 'mrt-row-actions': { size: 240 } },
      renderRowActions: ({ row }) => {
        const item = row.original;
        return (
          <Group wrap="nowrap">
            <EditActionIcon onClick={() => onRowClick(item)} />
            <CopyActionIcon onClick={() => onCopyClick(item)} />
            <BasicActionIcon
              label={item.complete ? 'Mark Incomplete' : 'Mark Complete'}
              color={item.complete ? 'green' : 'gray'}
              onClick={(e) => {
                e.stopPropagation();
                toggleComplete.mutate({
                  scope: { jobId: Number(jobId) },
                  itemId: item.id,
                });
              }}
            >
              {item.complete ? (
                <IconCircleX size={18} />
              ) : (
                <IconCircleCheckFilled size={18} />
              )}
            </BasicActionIcon>
            <MilestoneMoveActionIcons {...getMoveProps(item)} />
          </Group>
        );
      },

      renderTopToolbarCustomActions: () => (
        <Group>
          <CreateButton onClick={onCreateClick}>Add New Item</CreateButton>
        </Group>
      ),
      enableEditing: false,
      enableClickToCopy: false,
      columns: columns,
      data: data,
      manualPagination: false,
      manualSorting: false,
      manualFiltering: false,
    },
    getRowId: (row) => String(row.id),
    totalCount: data.length,
    isLoading: loading,
    isFetching: false,
    isError: false,
    ...tableExports,
  });

  return table;
};
